<template>
  <Password
    :ref="fieldId"
    v-model="data"
    fluid
    :toggle-mask="globalConfigStore.showPasswordEnabled"
    :disabled="props.disabled"
    auto-complete="off"
    :auto-focus="props.autoFocus"
    @enter="onEnter"
  >
    <template #inline-prefix>
      <InputIcon>
        <OutlinedIcon icon="key" class="text-lg" />
      </InputIcon>
    </template>

    <template #inline-sufix>
      <InputIcon v-if="keyboardEnabledComputed">
        <Button variant="outlined" @click="toggleKeyboard">
          <OutlinedIcon icon="keyboard" class="text-lg" />
        </Button>
      </InputIcon>
    </template>
  </Password>

  <Popover
    :ref="popoverId"
    :pt="{
      root: 'max-w-[360px] md:max-w-[390px] w-full',
      content: 'p-0',
    }"
  >
    <SimpleKeyboard v-model="data" />
  </Popover>
</template>

<script setup lang="ts">
import { useDesktopDetector } from "@common/hooks";
import { OutlinedIcon } from "@common/ui";
// biome-ignore lint/style/useImportType:
import { Button } from "@common/ui/atoms/Buttons/Button";
import { Password } from "@common/ui/atoms/Form/Password";
import { SimpleKeyboard } from "@common/ui/atoms/Misc/SimpleKeyboard";
import { useGlobalConfigStore } from "@shared/stores/useGlobalConfigStore.ts";
import InputIcon from "primevue/inputicon";
// biome-ignore lint/style/useImportType:
import Popover from "primevue/popover";
import { computed, useId, useTemplateRef } from "vue";

// Props
const props = defineProps({
  autoFocus: {
    type: Boolean,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
  },
});

// Models
const data = defineModel<string>({ required: true });

// Emits
const emit = defineEmits<{
  (e: "enter"): void;
}>();

// Constants
const fieldId = useId();
const popoverId = useId();

// Variables
const globalConfigStore = useGlobalConfigStore();
const passwordInput = useTemplateRef<InstanceType<typeof Password>>(fieldId);
const popoverKeyboard = useTemplateRef<InstanceType<typeof Popover>>(popoverId);
const desktopDetector = useDesktopDetector();

// Computed
const keyboardEnabledComputed = computed(() => {
  return globalConfigStore.keyboardEnabled && !props.disabled && desktopDetector.value;
});

// Methods
const getInputElement = () => {
  const el = passwordInput.value?.$el as HTMLDivElement | null;

  if (el) {
    return el.getElementsByTagName("input")[0];
  }
};

const focus = () => {
  getInputElement()?.focus();
};

const onEnter = () => {
  emit("enter");
};

const toggleKeyboard = (event: Event) => {
  popoverKeyboard.value?.toggle(event, getInputElement()!);
};

defineExpose({
  focus,
});
</script>
